"use client";
import { Tag, Typography } from "antd";

const { Text } = Typography;

const EditPreview = (props) => {
  const { formData, currencySymbol } = props;

  const formatFullDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const data = [
    {
      section: "Product Information",
      items: [
        {
          name: "Product Code",
          value: formData?.productInformation?.productCode
            ? formData.productInformation.productCode
            : "-",
        },
        {
          name: "Product Name",
          value: formData?.productInformation?.productName
            ? formData.productInformation.productName
            : "-",
        },
        {
          name: "Product Category",
          value: formData?.productInformation?.productCategoryLabel
            ? formData.productInformation.productCategoryLabel
            : "-",
          isTag: true,
        },
        {
          name: "Currency",
          value: formData?.productInformation?.currencyLabel
            ? formData.productInformation.currencyLabel
            : "-",
          isTag: true,
        },
        {
          name: "Issue Type",
          value: formData?.productInformation?.issueTypeLabel
            ? formData.productInformation.issueTypeLabel
            : "-",
          isTag: true,
        },
        {
          name: "Bond Type",
          value: formData?.productInformation?.bondTypeLabel
            ? formData.productInformation.bondTypeLabel
            : "-",
          isTag: true,
        },
        {
          name: "Custodian",
          value: formData?.productInformation?.custodianLabel
            ? formData.productInformation.custodianLabel
            : "-",
          isTag: true,
        },
        {
          name: "Accounting Classification",
          value: formData?.productInformation?.accountingClassificationLabel
            ? formData.productInformation.accountingClassificationLabel
            : "-",
          isTag: true,
        },
        {
          name: "Start Date",
          value: formatFullDate(formData?.productInformation?.startDate),
        },
        {
          name: "End Date",
          value: formatFullDate(formData?.productInformation?.endDate),
        },
      ],
    },
    {
      section: "Issuer Details",
      items: [
        {
          name: "Issuer Name",
          value: formData?.issuerDetails?.issuerName
            ? formData.issuerDetails.issuerName
            : "-",
          isTag: true,
        },
        {
          name: "Issuer Type",
          value: formData?.issuerDetails?.issuerTypeLabel
            ? formData.issuerDetails.issuerTypeLabel
            : "-",
          isTag: true,
        },
        {
          name: "Credit Rating (Fitch)",
          value: formData?.issuerDetails?.creditRating
            ? formData.issuerDetails.creditRating
            : "-",
          isTag: true,
        },
        {
          name: "Country of Issue",
          value: formData?.issuerDetails?.countryLabel
            ? formData.issuerDetails.countryLabel
            : "-",
          isTag: true,
        },
      ],
    },
    {
      section: "Bond Features",
      items: [
        {
          name: `Par Value/Cost Per Unit (${currencySymbol})`,
          value: formData?.bondFeatures?.faceValue
            ? formData.bondFeatures.faceValue?.toLocaleString()
            : "-",
        },
        {
          name: "Issue Price",
          value: formData?.bondFeatures?.issuePriceLabel
            ? formData.bondFeatures.issuePriceLabel
            : "-",
          isTag: true,
        },
        {
          name: "Coupon Rate Option",
          value: formData?.bondFeatures?.couponRateLabel
            ? formData.bondFeatures.couponRateLabel
            : "-",
          isTag: true,
        },
        {
          name: "Coupon Frequency",
          value: formData?.bondFeatures?.couponFrequencyLabel
            ? formData.bondFeatures.couponFrequencyLabel
            : "-",
          isTag: true,
        },
        {
          name: "Maturity Date",
          value: formData?.bondFeatures?.maturityDate
            ? formatFullDate(formData?.bondFeatures?.maturityDate)
            : "-",
        },
        {
          name: "Tenor",
          value: formData?.bondFeatures?.tenor
            ? formData.bondFeatures.tenor
            : "-",
        },
        {
          name: "Callable",
          value: formData.bondFeatures?.callable ?? "-",
          isTag: true,
        },
        {
          name: "Putable",
          value: formData.bondFeatures?.putable ?? "-",
          isTag: true,
        },
        {
          name: "Convertible",
          value: formData.bondFeatures?.convertible ?? "-",
          isTag: true,
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Repayment Type",
              isTagGroup: true,
              fields: [
                {
                  value: formData?.bondFeatures?.repayment ?? "-",
                  isTag: true,
                },
                {
                  value: formData?.bondFeatures?.repaymentTypeLabel || "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      section: "Interest Settings",
      items: [
        {
          name: "Interest Calculation Basis",
          value: formData?.interestSettings?.interestCalculationBasisLabel
            ? formData.interestSettings.interestCalculationBasisLabel
            : "-",
          isTag: true,
        },
        {
          name: "Interest Start Date",
          value: formData?.interestSettings?.interestStartDateLabel
            ? formData.interestSettings.interestStartDateLabel
            : "-",
          isTag: true,
        },
        {
          name: "First Coupon Date",
          value: formData?.interestSettings?.firstCouponDateLabel
            ? formData.interestSettings.firstCouponDateLabel
            : "-",
          isTag: true,
        },
        {
          name: "Interest Payment Mode",
          value: formData?.interestSettings?.interestPaymentModeLabel
            ? formData.interestSettings.interestPaymentModeLabel
            : "-",
          isTag: true,
        },
      ],
    },
    {
      section: "Tax & Charges",
      items: [
        {
          isInlineGroup: true,
          group: [
            {
              name: "Tax Group",
              isTagGroup: true,
              fields: [
                {
                  value: formData.taxAndCharges?.taxGroup?.enabled ?? "-",
                  isTag: true,
                },
                {
                  value: formData.taxAndCharges?.taxGroup?.taxLabel || "-",
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Processing Charges",
              isTagGroup: true,
              fields: [
                {
                  value: formData.taxAndCharges?.setup?.enabled ?? "-",
                  isTag: true,
                },
                {
                  value: formData.taxAndCharges?.setup?.chargeLabel || "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Redemption Charges",
              isTagGroup: true,
              fields: [
                {
                  value: formData.taxAndCharges?.redemption?.enabled ?? "-",
                  isTag: true,
                },
                {
                  value: formData.taxAndCharges?.redemption?.chargeLabel || "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Stamp Duty Charges",
              isTagGroup: true,
              fields: [
                {
                  value: formData.taxAndCharges?.transfer?.enabled ?? "-",
                  isTag: true,
                },
                {
                  value: formData.taxAndCharges?.transfer?.chargeLabel || "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Brokerage/Agent Commission",
              isTagGroup: true,
              fields: [
                {
                  value: formData.taxAndCharges?.brokerage?.enabled ?? "-",
                  isTag: true,
                },
                {
                  value:
                    formData.taxAndCharges?.brokerage?.commissionLabel || "-",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      section: "Accounting",
      items: [
        {
          isInlineGroup: true,
          group: [
            {
              name: "Investment - Deal Booking",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(formData.accounting?.investmentGLLabel) &&
                    formData.accounting.investmentGLLabel[0]
                      ? formData.accounting.investmentGLLabel[0] ?? "-"
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(formData.accounting?.investmentGLLabel) &&
                    formData.accounting.investmentGLLabel[1]
                      ? formData.accounting.investmentGLLabel[1] ?? "-"
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Interest Accrual Posting",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(
                      formData.accounting?.accruedInterestGLLabel
                    ) && formData.accounting.accruedInterestGLLabel[0]
                      ? formData.accounting.accruedInterestGLLabel[0]
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(
                      formData.accounting?.accruedInterestGLLabel
                    ) && formData.accounting.accruedInterestGLLabel[1]
                      ? formData.accounting.accruedInterestGLLabel[1]
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Investment - Redemption (Customer Payable)",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(formData.accounting?.settlementGLLabel) &&
                    formData.accounting.settlementGLLabel[0]
                      ? formData.accounting.settlementGLLabel[0]
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(formData.accounting?.settlementGLLabel) &&
                    formData.accounting.settlementGLLabel[1]
                      ? formData.accounting.settlementGLLabel[1]
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Income Interest Posting",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(formData.accounting?.interestIncomeGLLabel) &&
                    formData.accounting.interestIncomeGLLabel[0]
                      ? formData.accounting.interestIncomeGLLabel[0]
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(formData.accounting?.interestIncomeGLLabel) &&
                    formData.accounting.interestIncomeGLLabel[1]
                      ? formData.accounting.interestIncomeGLLabel[1]
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Income Fees Posting",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(formData.accounting?.feeGLLabel) &&
                    formData.accounting.feeGLLabel[0]
                      ? formData.accounting.feeGLLabel[0]
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(formData.accounting?.feeGLLabel) &&
                    formData.accounting.feeGLLabel[1]
                      ? formData.accounting.feeGLLabel[1]
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
        {
          isInlineGroup: true,
          group: [
            {
              name: "Income Penalty Posting",
              isTagGroup: true,
              fields: [
                {
                  value:
                    Array.isArray(formData.accounting?.penaltyGLLabel) &&
                    formData.accounting.penaltyGLLabel[0]
                      ? formData.accounting.penaltyGLLabel[0]
                      : "-",
                  isTag: true,
                },
                {
                  value:
                    Array.isArray(formData.accounting?.penaltyGLLabel) &&
                    formData.accounting.penaltyGLLabel[1]
                      ? formData.accounting.penaltyGLLabel[1]
                      : "-",
                  isTag: true,
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  const getDisplayText = (label) => {
    if (label === null || label === undefined || label === "") return "-";
    if (typeof label === "string") return label;
    if (typeof label === "object" && "text" in label) return label.text;
    return label;
  };

  const renderTag = (value) => {
    if (value === true) {
      return (
        <Tag
          style={{
            backgroundColor: "#ECFDF3",
            border: "1px solid #ABEFC6",
            borderRadius: "16px",
            padding: "2px 12px",
          }}
        >
          <Text
            style={{
              color: "#067647",
              fontWeight: 600,
            }}
          >
            Yes
          </Text>
        </Tag>
      );
    }

    if (value === false) {
      return (
        <Tag
          style={{
            backgroundColor: "#F9FAFB",
            border: "1px solid #EAECF0",
            borderRadius: "16px",
            padding: "2px 12px",
            textTransform: "capitalize",
          }}
        >
          <Text
            style={{
              color: "#344054",
              fontWeight: 600,
            }}
          >
            No
          </Text>
        </Tag>
      );
    }

    if (
      value === "-" ||
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return (
        <Text style={{ fontWeight: 500, color: "#6b7280" }}>
          {value || "-"}
        </Text>
      );
    }

    // Default tag for select fields
    return (
      <Tag
        style={{
          backgroundColor: "#FDF4FF",
          border: "1px solid #F6D0FE",
          borderRadius: "16px",
          padding: "2px 12px",
        }}
      >
        <Text
          style={{
            textTransform: "capitalize",
            color: "#9F1AB1",
            fontWeight: 600,
          }}
        >
          {value}
        </Text>
      </Tag>
    );
  };

  const renderRow = (item, index, section) => {
    const isLastItem = index === (data[section]?.items?.length || 0) - 1;

    if (item.isInlineGroup && Array.isArray(item.group)) {
      return (
        <div
          key={`inline-group-${section}-${index}`}
          style={{
            display: "flex",
            borderBottom: isLastItem ? "none" : "1px solid #f0f0f0",
          }}
        >
          {item.group.map((fieldGroup, groupIndex) => (
            <>
              <div
                key={`group-${groupIndex}`}
                style={{
                  width: "50%",
                  backgroundColor: "#f9f9f9",
                  padding: "16px 24px",
                  fontWeight: "500",
                  borderRight: "1px solid #f0f0f0",
                }}
              >
                {fieldGroup.name}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  padding: "16px 24px",
                  width: "50%",
                  backgroundColor: "#fff",
                }}
              >
                {fieldGroup.fields.map((subField) => (
                  <div
                    key={subField.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 500,
                    }}
                  >
                    {subField.isTag ? (
                      renderTag(getDisplayText(subField.value))
                    ) : (
                      <span>{getDisplayText(subField.value)}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ))}
        </div>
      );
    }

    return (
      <div
        key={item.key || item.name}
        style={{
          display: "flex",
          borderBottom: isLastItem ? "none" : "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#f9f9f9",
            fontWeight: "500",
            borderRight: "1px solid #f0f0f0",
          }}
        >
          {item.name}
        </div>
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#fff",
          }}
        >
          {item.isTag ? (
            renderTag(getDisplayText(item.value))
          ) : (
            <Text style={{ textTransform: "capitalize", fontWeight: 600 }}>
              {getDisplayText(item.value)}
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <h1
        style={{
          fontSize: "24px",
          fontWeight: "700",
          margin: 0,
          marginBottom: "24px",
        }}
      >
        Preview
      </h1>

      {data.map((section, sectionIndex) => (
        <div key={section.section}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            {section.section}
          </h2>
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
              overflow: "hidden",
              marginBottom: "24px",
            }}
          >
            {section.items.map((item, index) =>
              renderRow(item, index, sectionIndex)
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditPreview;
